import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { DotSphereSpinner } from '@/components/ui/DotSphereSpinner'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { SendHorizontal, FileCode, Trash2, Download, Loader2, ArrowLeft, Eye, Code2, Save, Copy, Check, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore, useUser } from '@/app/store/auth'
import { useAiChatPanel } from '@/contexts/AiChatPanelContext'
import { iacApi, type IaCContext, type IaCProject, type IaCFile } from '../api'
import { useObject } from '@/features/objects/hooks'
import { buildIconPath } from '@/lib/imagePaths'
import { DeploymentPanel } from '../components/DeploymentPanel'
import { IaCOverviewFlow } from '../components/IaCOverviewFlow'
import { ArchitectureFlow } from '../components/architecture/ArchitectureFlow'
import '@/features/ai-chat/core/styles/ChatMessageArea.css'

// Constrain code blocks and pre elements within the chat panel
const iacStyles = document.createElement('style')
iacStyles.textContent = `
.iac-chat .message-bubble { max-width: 100%; overflow: hidden; font-size: 13px; line-height: 1.5; }
.iac-chat .message-bubble p { font-size: 13px; margin-bottom: 4px; }
.iac-chat .message-bubble li { font-size: 13px; }
.iac-chat .message-bubble h1 { font-size: 16px; }
.iac-chat .message-bubble h2 { font-size: 15px; }
.iac-chat .message-bubble h3 { font-size: 14px; }
.iac-chat .message-bubble pre { max-width: 100%; overflow-x: auto; }
.iac-chat .message-bubble .code-block-wrapper { max-width: 100%; overflow: hidden; }
.iac-chat .message-bubble .code-block { max-width: 100%; overflow-x: auto; font-size: 11px; }
.iac-chat .message-bubble table { font-size: 12px; }
.iac-chat .message-bubble img { max-width: 100%; }
.iac-chat .message-bubble a { text-decoration: none; }
.iac-chat .message-bubble u { text-decoration: none; }
.iac-chat .message-sender { font-size: 11px; }
.iac-chat .message-container { padding: 6px 12px; }
`
if (!document.getElementById('iac-styles')) { iacStyles.id = 'iac-styles'; document.head.appendChild(iacStyles) }

interface ChatMessage {
    role: 'user' | 'assistant' | 'system'
    content: string
    timestamp?: string
    codeBlocks?: Array<{ filename: string; code: string; language: string }>
}

function setupMarked() {
    const renderer = new marked.Renderer()
    renderer.code = function (token: any) {
        const code = typeof token === 'string' ? token : (token.text || '')
        const lang = (typeof token === 'string' ? '' : token.lang || '').toLowerCase()
        const hide = !lang || lang === 'text' || lang === 'plain' || lang === 'code'
        return `<div class="code-block-wrapper"><${hide ? '' : `div class="code-block-header">${lang}</div><`}pre class="code-block"><code>${code}</code></pre></div>`
    }
    renderer.codespan = function (token: any) {
        return `<code class="inline-code">${typeof token === 'string' ? token : (token.text || '')}</code>`
    }
    renderer.listitem = function (token: any) {
        const html = (token.tokens && (this as any).parser) ? (this as any).parser.parse(token.tokens) : (typeof token === 'string' ? token : (token.text || ''))
        return `<li>${html.replace(/^<p>|<\/p>\s*$/g, '').trim()}</li>\n`
    }
    marked.setOptions({ breaks: false, gfm: true, renderer, pedantic: false })
}

function renderMarkdown(content: string): string {
    const lines = content.split('\n')
    const processed: string[] = []
    let inTable = false
    for (const line of lines) {
        const isTableRow = line.trim().startsWith('|') && line.trim().endsWith('|')
        if (isTableRow) { inTable = true; processed.push(line) }
        else if (inTable && line.trim() === '') { continue }
        else { inTable = false; processed.push(line) }
    }
    return DOMPurify.sanitize(marked.parse(processed.join('\n')) as string)
}

function extractOptions(content: string): { cleanContent: string; options: string[]; multiSelect: boolean } {
    // Check for [MULTI-OPTIONS] (multi-select) or [OPTIONS] (single-select)
    const multiMatch = content.match(/\[MULTI-OPTIONS\]\s*(.+?)$/m)
    const singleMatch = content.match(/\[OPTIONS\]\s*(.+?)$/m)
    const match = multiMatch || singleMatch
    const multiSelect = !!multiMatch
    if (!match) return { cleanContent: content, options: [], multiSelect: false }
    const options = match[1].split('|').map(o => o.trim()).filter(Boolean)
    // Also detect multi-select keywords in options like "compliance", "requirements"
    const isLikelyMulti = multiSelect || options.some(o => ['PCI-DSS', 'SOX', 'HIPAA', 'ISO 27001', 'GDPR'].includes(o))
    const cleanContent = content.replace(/\[(MULTI-)?OPTIONS\]\s*.+$/m, '').trim()
    return { cleanContent, options, multiSelect: isLikelyMulti }
}

// Strip code blocks from content for chat display — they show in the right panel instead
function stripCodeBlocks(content: string): string {
    // Step 1: Replace complete code blocks that have # File: headers
    let cleaned = content.replace(/```\w*\s*\n#\s*(?:File:\s*)?(.+?)\n[\s\S]*?```/g, (_, filename) => `\n✅ **${filename.trim()}** generated\n`)
    // Step 2: Replace complete code blocks with ## headings
    cleaned = cleaned.replace(/```\w*\s*\n##\s*(.+?)\n[\s\S]*?```/g, (_, heading) => `\n✅ **${heading.trim()}** generated\n`)
    // Step 3: Remove ALL remaining complete code blocks (no header — bash, json, anything)
    cleaned = cleaned.replace(/```\w*\n[\s\S]*?```/g, '')
    cleaned = cleaned.replace(/```[\s\S]*?```/g, '')
    // Step 4: Handle partial/in-progress code blocks (streaming — block started but not closed)
    const openBackticks = (cleaned.match(/```/g) || []).length
    if (openBackticks % 2 !== 0) {
        // There's an unclosed code block — strip everything from the opening ``` to end
        const lastOpen = cleaned.lastIndexOf('```')
        const beforeBlock = cleaned.substring(0, lastOpen)
        // Try to extract filename from partial block
        const partial = cleaned.substring(lastOpen)
        const fileMatch = partial.match(/```\w*\s*\n#\s*(?:File:\s*)?(.+?)[\n\r]/)
        const label = fileMatch ? `⏳ Generating **${fileMatch[1].trim()}**...` : '⏳ Generating file...'
        cleaned = beforeBlock + '\n' + label + '\n'
    }
    // Step 5: Strip deployment guide sections and long bash command sequences
    cleaned = cleaned.replace(/#{1,3}\s*(?:Prerequisites|Pre-flight|Deployment Guide|Next Steps|Troubleshooting|File Structure|Destroy)[\s\S]*?(?=\n#{1,3}\s|\n\*\*[A-Z]|$)/gi, '')
    cleaned = cleaned.replace(/(?:^|\n)(?:\s*(?:aws|terraform|export|chmod|cd|echo|curl|bash|cat|cp|\.\/)\s.*\n?){3,}/gm, '\n')
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim()
    return cleaned || '*Generating...*'
}

function extractCodeBlocks(content: string): Array<{ filename: string; code: string; language: string }> {
    const blocks: Array<{ filename: string; code: string; language: string }> = []
    // Match code blocks with:
    //   # File: filename.tf
    //   ## Section heading (for DEPLOYMENT-GUIDE sections)
    //   or no header (auto-named)
    const regex = /```(\w*)\s*\n(?:(?:#\s*(?:File:\s*)?|##\s*)(.+?)\n)?([\s\S]*?)```/g
    let match
    while ((match = regex.exec(content)) !== null) {
        const lang = match[1] || 'hcl'
        const header = match[2]?.trim()
        // Skip small inline code examples (bash one-liners, command examples)
        const code = match[3].trim()
        if (!header && code.split('\n').length <= 3 && code.length < 200) continue
        const filename = header || `file_${blocks.length + 1}.${lang === 'hcl' || lang === 'terraform' ? 'tf' : lang}`
        blocks.push({ filename, code, language: lang })
    }
    return blocks
}

function formatTime(ts?: string): string {
    if (!ts) return ''
    return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)
    return (
        <button className="text-muted-foreground hover:text-foreground transition-colors p-1" title="Copy"
            onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}>
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
    )
}

export function IaCPage() {
    const { mainId, scenario: scenarioParam } = useParams<{ mainId: string; scenario: string }>()
    const scenario = scenarioParam ? parseInt(scenarioParam) : 1
    const navigate = useNavigate()
    const { t } = useTranslation()
    const searchParams = new URLSearchParams(window.location.search)
    const projectId = searchParams.get('pid')
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
    const user = useUser()
    const aiChatPanel = useAiChatPanel()
    const { data: objectData } = useObject(mainId || null)
    const chatRef = useRef<HTMLDivElement>(null)
    const scrolledUp = useRef(false)
    const agentWorkingRef = useRef(false)

    const [input, setInput] = useState('')
    const [messages, setMessages] = useState<ChatMessage[]>([])
    const [sending, setSending] = useState(false)
    const [agentWorking, setAgentWorking] = useState(false)
    // Clara assists with deploy/fix iteration (done in the left chat), so it lives
    // on the left — but only becomes available once deployment has started or the
    // user has downloaded the Terraform to deploy themselves.
    const [deployStatus, setDeployStatus] = useState('not_deployed')
    const [downloaded, setDownloaded] = useState(false)
    const claraAvailable = downloaded || (!!deployStatus && deployStatus !== 'not_deployed')
    const [navWarning, setNavWarning] = useState<{ href: string } | null>(null)
    const [statusMsgs, setStatusMsgs] = useState<string[]>([])
    const [sid, setSid] = useState<string | null>(null)
    const [activeIdx, setActiveIdx] = useState<number | null>(null)
    const [files, setFiles] = useState<Array<{ filename: string; code: string; language: string }>>([])
    const [rightTab, setRightTab] = useState<'code' | 'vis'>('code')
    // Architecture overview — derived on demand from the current Terraform (see below).
    const [archData, setArchData] = useState<any | null>(null)
    const [archHash, setArchHash] = useState<string>('')
    const [archBuilding, setArchBuilding] = useState(false)
    const [archError, setArchError] = useState<string | null>(null)

    const tfFilesForArch = useMemo(
        () => files.filter((f) => /\.tf$/i.test(f.filename)).map((f) => ({ filename: f.filename, code: f.code || '' })),
        [files]
    )
    const currentArchHash = useMemo(() => {
        const s = tfFilesForArch.map((f) => `${f.filename}:${f.code}`).join('|')
        let h = 0
        for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
        return String(h)
    }, [tfFilesForArch])
    const archStale = !!archData && archHash !== currentArchHash

    const buildArchitecture = useCallback(async () => {
        if (tfFilesForArch.length === 0) return
        setArchBuilding(true)
        setArchError(null)
        try {
            const res = await iacApi.deriveArchitecture(tfFilesForArch)
            const data = (res.data as any)?.data
            if (data && Array.isArray(data.containers) && Array.isArray(data.nodes)) {
                setArchData(data)
                setArchHash(currentArchHash)
            } else {
                setArchError('Could not derive the architecture from the Terraform.')
            }
        } catch (e: any) {
            setArchError(e?.response?.data?.error || e?.message || 'Failed to build architecture')
        } finally {
            setArchBuilding(false)
        }
    }, [tfFilesForArch, currentArchHash])

    // Auto-derive once when the Overview is opened and there's no diagram yet.
    useEffect(() => {
        if (rightTab === 'vis' && tfFilesForArch.length > 0 && !archData && !archBuilding && !archError) {
            buildArchitecture()
        }
    }, [rightTab, tfFilesForArch.length, archData, archBuilding, archError, buildArchitecture])
    const [loaded, setLoaded] = useState(false)
    const [showRestart, setShowRestart] = useState(false)
    const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set())
    const [verified, setVerified] = useState(false)
    // terraform validate result (authoritative once it has run)
    const [validating, setValidating] = useState(false)
    const [validation, setValidation] = useState<{
        ran: boolean; valid: boolean; attempts: number; errorCount: number; warningCount: number;
        diagnostics: Array<{ severity: string; summary: string; detail: string; file: string | null; line: number | null }>;
        changedFiles: string[];
    } | null>(null)
    const validatedSigRef = useRef<string>('')   // signature of the last validated file-set
    const armValidationRef = useRef(false)        // set after a generation turn; gates auto-validate

    useEffect(() => { setupMarked() }, [])
    useEffect(() => { if (!scrolledUp.current && chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight }, [messages, statusMsgs])

    // Detect verification completion from assistant messages
    useEffect(() => {
        if (verified) return
        const lastAssistant = [...messages].reverse().find(m => m.role === 'assistant')
        if (lastAssistant?.content && (
            lastAssistant.content.includes('VERIFICATION SUMMARY') ||
            lastAssistant.content.includes('Cross-provider verification result') ||
            lastAssistant.content.includes('verification result: PASS') ||
            lastAssistant.content.includes('Verdict') && lastAssistant.content.includes('PASS')
        )) {
            setVerified(true)
        }
    }, [messages, verified])

    // Run `terraform validate` server-side after a generation turn. The backend
    // auto-fixes validate errors via the model and returns the corrected files,
    // so the user receives already-validated Terraform (fewer deploy iterations).
    const runValidation = useCallback(async (sig: string) => {
        setValidating(true)
        try {
            const res = await iacApi.validate(files)
            const d: any = res.data
            const corrected = Array.isArray(d.files)
                ? d.files.map((f: any) => ({ filename: f.filename, code: f.code ?? f.content ?? '', language: f.filename?.endsWith('.tf') ? 'hcl' : 'text' }))
                : null
            // Anchor the validated signature to the FINAL file-set so the effect doesn't loop.
            validatedSigRef.current = corrected ? corrected.map((f: any) => `${f.filename}:${(f.code || '').length}`).join('|') : sig
            setValidation({ ran: !!d.ran, valid: !!d.valid, attempts: d.attempts || 0, errorCount: d.errorCount || 0, warningCount: d.warningCount || 0, diagnostics: d.diagnostics || [], changedFiles: d.changedFiles || [] })
            if (d.ran) setVerified(!!d.valid)
            if (corrected && d.changedFiles?.length) {
                setFiles(corrected)
                setTimeout(() => { if (!saveMut.isPending) saveMut.mutate() }, 400)
            }
            if (d.ran) {
                const errs = (d.diagnostics || []).filter((x: any) => x.severity === 'error')
                const content = d.valid
                    ? (d.changedFiles?.length
                        ? `✅ **terraform validate passed** — auto-fixed ${d.changedFiles.length} issue(s) in ${d.changedFiles.join(', ')}. The corrected files are ready to deploy.`
                        : `✅ **terraform validate passed** — no issues found. Ready to deploy.`)
                    : `⚠️ **terraform validate found ${d.errorCount} error(s)** that need attention:\n\n${errs.slice(0, 10).map((x: any, i: number) => `${i + 1}. ${x.file ? `\`${x.file}${x.line ? `:${x.line}` : ''}\` — ` : ''}${x.summary}`).join('\n')}`
                setMessages(p => [...p, { role: 'assistant', content, timestamp: new Date().toISOString() }])
            }
        } catch {
            validatedSigRef.current = sig  // infra error — don't hammer-retry
        } finally {
            setValidating(false)
        }
    }, [files])

    // After a generation turn (armed in send's finally), validate once the agent
    // is idle and the .tf file-set has actually changed.
    useEffect(() => {
        if (agentWorking || validating) return
        if (!armValidationRef.current) return
        const tf = files.filter(f => /\.tf$/i.test(f.filename))
        const sig = files.map(f => `${f.filename}:${(f.code || '').length}`).join('|')
        if (tf.length === 0 || validatedSigRef.current === sig) { armValidationRef.current = false; return }
        armValidationRef.current = false
        runValidation(sig)
    }, [agentWorking, files, validating, runValidation])

    // Authoritative "ready to deploy" signal: terraform validate result once it
    // has run, otherwise fall back to the agent's self-reported verification.
    const verifyPassed = validation?.ran ? validation.valid : verified

    // Keep ref in sync for event handlers
    useEffect(() => { agentWorkingRef.current = agentWorking }, [agentWorking])

    // Warn user not to navigate away while agent is working
    useEffect(() => {
        if (!agentWorking) return

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }

        // Push a dummy history entry so back button triggers popstate instead of leaving
        window.history.pushState({ iacGuard: true }, '')

        const handlePopState = () => {
            if (agentWorkingRef.current) {
                window.history.pushState({ iacGuard: true }, '')
                setNavWarning({ href: 'back' })
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload)
        window.addEventListener('popstate', handlePopState)
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
            window.removeEventListener('popstate', handlePopState)
        }
    }, [agentWorking])

    // Intercept sidebar/menu link clicks while agent is working
    useEffect(() => {
        if (!agentWorking) return
        const handler = (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest('a[href], [data-nav-link]')
            if (!target) return
            const href = target.getAttribute('href')
            if (!href || href === '#') return
            if (href === window.location.pathname) return
            e.preventDefault()
            e.stopPropagation()
            setNavWarning({ href })
        }
        document.addEventListener('click', handler, true)
        return () => document.removeEventListener('click', handler, true)
    }, [agentWorking])


    // Remove container padding/maxWidth to use full screen width (same as visualization page)
    useEffect(() => {
        const main = document.querySelector('main')
        const container = main?.querySelector(':scope > div')
        if (main) { main.style.overflow = 'hidden'; main.style.padding = '0' }
        if (container) { (container as HTMLElement).style.padding = '0'; (container as HTMLElement).style.maxWidth = 'none'; (container as HTMLElement).style.height = '100%' }
        return () => {
            if (main) { main.style.overflow = ''; main.style.padding = '' }
            if (container) { (container as HTMLElement).style.padding = ''; (container as HTMLElement).style.maxWidth = ''; (container as HTMLElement).style.height = '' }
        }
    }, [])

    const { data: ctx, isLoading } = useQuery({
        queryKey: ['iac-context', mainId],
        queryFn: async () => { const r = await iacApi.getContext(mainId!, scenario); const d = r.data as any; return (d?.data || d) as IaCContext },
        enabled: !!mainId && isAuthenticated,
    })

    const { data: projData } = useQuery({
        queryKey: ['iac-projects', mainId],
        queryFn: async () => { const r = await iacApi.listProjects(mainId!); const d = r.data as any; return (d?.projects || []) as IaCProject[] },
        enabled: !!mainId && isAuthenticated,
    })

    const proj = projectId
        ? (projData || []).find((p: any) => p.id === Number(projectId)) || (projData || [])[0] || null
        : (projData || [])[0] || null

    useEffect(() => {
        if (proj?.files) {
            const ex = proj.files.map((f: IaCFile) => ({ filename: f.filePath, code: f.content, language: f.filePath.endsWith('.tf') ? 'hcl' : 'text' }))
            if (ex.length > 0 && files.length === 0) setFiles(ex)
        }
    }, [proj])

    useEffect(() => {
        if (!ctx || sid) return
        ;(async () => {
            // Read JSON only if the response actually is JSON; otherwise log a useful
            // diagnostic. Without this guard a stale CloudFront 404→/index.html
            // (HTML body, 200 status) throws "Unexpected token '<'" and breaks init.
            const readJsonOrWarn = async (label: string, res: Response): Promise<any | null> => {
                const ct = res.headers.get('content-type') || ''
                if (!ct.toLowerCase().includes('application/json')) {
                    const body = await res.text().catch(() => '')
                    console.warn(`IaC init: ${label} returned non-JSON (status=${res.status}, content-type="${ct}"). Body head:`, body.slice(0, 200))
                    return null
                }
                try { return await res.json() } catch (e) {
                    console.warn(`IaC init: ${label} JSON parse failed`, e)
                    return null
                }
            }

            try {
                const r = await fetch('/api/ai/chat/iac-session', { method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId: user?.id ? String(user.id) : 'system', mainId, scenario, projectId: projectId || undefined, projectDescription: proj?.description || undefined, serviceName: ctx.serviceName || mainId, hierarchyContext: ctx }) })
                if (!r.ok) {
                    console.warn(`IaC init: iac-session HTTP ${r.status}`)
                    return
                }
                const d = await readJsonOrWarn('iac-session', r)
                if (!d?.sessionId) return
                setSid(d.sessionId)
                try {
                    const h = await fetch(`/api/ai/chat-history/session/${d.sessionId}/messages`)
                    if (h.ok) {
                        const hd = await readJsonOrWarn('chat-history/messages', h)
                        const saved = (hd?.messages || []).map((m: any) => ({ role: m.role, content: m.content, timestamp: m.timestamp || m.createdAt, codeBlocks: m.role === 'assistant' ? extractCodeBlocks(m.content || '') : undefined }))
                        if (saved.length > 0) { setMessages(saved); saved.forEach((m: any) => { if (m.codeBlocks?.length) addFiles(m.codeBlocks) }); setLoaded(true); return }
                    }
                } catch (e) { console.warn('IaC init: chat-history fetch failed', e) }
                setMessages([{ role: 'assistant', content: `Hierarchy loaded for **${ctx.serviceName}** — ${ctx.objectCount} objects, ${ctx.versionCount} backup versions.\n\nClick **Start Analysis** to begin.`, timestamp: new Date().toISOString() }])
            } catch (e) { console.error('IaC init failed:', e) }
        })()
    }, [ctx, sid])

    const addFiles = useCallback((blocks: Array<{ filename: string; code: string; language: string }>) => {
        setFiles(prev => {
            const u = [...prev]
            blocks.forEach(b => { const i = u.findIndex(f => f.filename === b.filename); i >= 0 ? u[i] = b : u.push(b) })
            // Sync files to session context so Agent SDK can read them
            if (sid && u.length > 0) {
                fetch(`/api/ai/chat/iac-session/${sid}/files`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ files: u.map(f => ({ filename: f.filename, code: f.code })) }),
                }).catch(() => {})
            }
            return u
        })
    }, [sid])

    // Use Agent SDK when files exist (deployment/troubleshooting phase)
    // Use standard orchestrator during initial generation (IaC agent needs code output format)
    const useAgentSDK = files.length > 0

    async function send(msg: string) {
        if (!msg.trim() || !sid) return
        setSending(true)
        setAgentWorking(true)
        setStatusMsgs([])
        scrolledUp.current = false

        // Add user message
        const ts = new Date().toISOString()
        setMessages(p => [...p, { role: 'user', content: msg, timestamp: ts }])
        setInput('')

        // If switching to Agent SDK, sync files first
        if (useAgentSDK && files.length > 0) {
            await fetch(`/api/ai/chat/iac-session/${sid}/files`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ files: files.map(f => ({ filename: f.filename, code: f.code })) }),
            }).catch(() => {})
        }

        let content = ''
        let addedAssistantMsg = false

        try {
            // Agent SDK for deployment help (reads files autonomously)
            // Standard orchestrator for code generation (IaC agent prompt)
            const endpoint = useAgentSDK ? '/api/ai/agent-sdk/message' : '/api/ai/chat/message'
            const r = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sessionId: sid, message: msg }),
            })
            if (!r.ok) throw new Error(`HTTP ${r.status}`)

            const reader = r.body?.getReader()
            const dec = new TextDecoder()
            let buf = ''

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break
                    buf += dec.decode(value, { stream: true })
                    const evts = buf.split('\n\n')
                    buf = evts.pop() || ''

                    for (const evt of evts) {
                        let type = '', data = ''
                        for (const ln of evt.split('\n')) {
                            if (ln.startsWith('event: ')) type = ln.slice(7).trim()
                            if (ln.startsWith('data: ')) data = ln.slice(6)
                        }
                        if (!data) continue

                        try {
                            const j = JSON.parse(data)
                            if (type === 'status' && j.status) {
                                setStatusMsgs(p => [...p, j.status])
                            } else if (j.content) {
                                content += j.content

                                // First content chunk: hide spinner, add assistant message
                                if (!addedAssistantMsg) {
                                    addedAssistantMsg = true
                                    setSending(false)
                                    setStatusMsgs([])
                                    setMessages(p => [...p, { role: 'assistant', content, timestamp: new Date().toISOString() }])
                                } else {
                                    // Subsequent chunks: update last message in place
                                    const c = content
                                    // Check for completed code blocks and extract them progressively
                                    const cb = extractCodeBlocks(c)
                                    if (cb.length > 0) addFiles(cb)
                                    setMessages(p => {
                                        const u = [...p]
                                        u[u.length - 1] = { ...u[u.length - 1], content: c, codeBlocks: cb.length > 0 ? cb : u[u.length - 1].codeBlocks }
                                        return u
                                    })
                                }
                            } else if (j.response) {
                                content = j.response
                            }
                        } catch {}
                    }
                }
            }

            // Finalize
            if (!content) content = 'No response received.'
            const cb = extractCodeBlocks(content)
            setMessages(p => {
                const u = [...p]
                if (addedAssistantMsg && u.length > 0) {
                    u[u.length - 1] = { ...u[u.length - 1], content, codeBlocks: cb.length > 0 ? cb : undefined }
                } else {
                    u.push({ role: 'assistant', content, timestamp: new Date().toISOString(), codeBlocks: cb.length > 0 ? cb : undefined })
                }
                return u
            })
            if (cb.length > 0) addFiles(cb)
        } catch (e: any) {
            setMessages(p => [...p, { role: 'assistant', content: `Error: ${e?.message || 'Failed'}`, timestamp: new Date().toISOString() }])
        } finally {
            setSending(false)
            setAgentWorking(false)
            setStatusMsgs([])
            // Arm a terraform-validate pass; the effect runs it only if the .tf
            // file-set actually changed this turn (no-op for plain Q&A turns).
            armValidationRef.current = true
            // Auto-save: persist any generated files after each agent response
            setTimeout(() => {
                setFiles(currentFiles => {
                    if (currentFiles.length > 0 && !saveMut.isPending) {
                        saveMut.mutate()
                    }
                    return currentFiles
                })
            }, 1000)
        }
    }

    const saveMut = useMutation({
        mutationFn: async () => {
            let pid = proj?.id
            if (!pid) { const r = await iacApi.createProject({ mainId: mainId!, name: `${ctx?.serviceName || mainId} - Terraform`, cloudPlatform: 'multi', scenario }); pid = (r.data as any)?.project?.id }
            if (!pid) throw new Error('No project'); for (const f of files) { if (f.filename && f.code) await iacApi.saveFile(pid, { filePath: f.filename, content: f.code }) }; return pid
        },
        onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['iac-projects', mainId] }); toast({ title: `${files.length} files saved` }) },
        onError: () => toast({ title: t('iac.saveFailed'), variant: 'destructive' }),
    })

    function download() {
        const c = files.map(f => `# File: ${f.filename}\n\n${f.code}\n`).join('\n\n')
        const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([c], { type: 'text/plain' })); a.download = `${mainId}-terraform.tf`; a.click()
    }

    const active = activeIdx !== null ? files[activeIdx] : null

    if (isLoading) return <div className="flex items-center justify-center h-full"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>

    return (
        <div className="flex flex-col h-full overflow-hidden" style={{ height: 'calc(100vh - var(--topbar-height, 56px))' }}>
            {/* Page header */}
            <div className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(-1)}>
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div>
                        <h1 className="text-lg font-semibold">{t('iac.title')}</h1>
                        <div className="flex items-center gap-2 mt-1.5">
                            {objectData?.iconFilename && (
                                <img src={buildIconPath(objectData.iconFilename)} alt="" className="h-6 w-6 object-contain" />
                            )}
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold">{mainId}</span>
                                    <span className="text-xs text-muted-foreground">— {ctx?.objectCount || 0} objects, {ctx?.versionCount || 0} versions</span>
                                </div>
                                <p className="text-xs text-muted-foreground">{objectData?.objectDescription || ctx?.serviceName || ''}</p>
                                {proj?.description && (
                                    <p className="text-xs text-blue-600 font-medium">{proj.description}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setShowRestart(true)}>
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5 text-blue-600" />New Project
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => saveMut.mutate()} disabled={files.length === 0 || saveMut.isPending}>
                        <Save className="h-3.5 w-3.5 mr-1.5 text-blue-600" />Save Project
                    </Button>
                </div>
            </div>

            {/* Agent working warning */}
            {agentWorking && (
                <div className="flex items-center gap-2 bg-amber-50 border-b border-amber-200 px-6 py-2 text-sm text-amber-800">
                    <Loader2 className="h-4 w-4 animate-spin flex-shrink-0" />
                    <span className="font-medium">{t('iac.agentWorking')}</span>
                </div>
            )}

            {/* Panels */}
            <div className="flex flex-1 overflow-hidden border-t">
            {/* LEFT: Chat */}
            <div className="iac-chat flex-1 flex flex-col border-r overflow-hidden">
                {/* Phase progress bar */}
                {messages.length > 1 && messages.filter(m => m.role === 'user').length > 0 && (() => {
                    const phases = [
                        { key: 'analyse', label: t('iac.phaseAnalyse', 'Analyse') },
                        { key: 'questions', label: t('iac.phaseQuestions', 'Questions') },
                        { key: 'plan', label: t('iac.phasePlan', 'Plan') },
                        { key: 'generate', label: t('iac.phaseGenerate', 'Generate') },
                        { key: 'verify', label: t('iac.phaseVerify', 'Verify') },
                    ]
                    const assistantMsgs = messages.filter(m => m.role === 'assistant')
                    const hasFiles = files.length > 0
                    const allContent = messages.map(m => m.content).join('\n')
                    const hasVerification = /VERIFICATION|verification|self-verification|cross-provider|SELF-VERIFICATION COMPLETE|sending to independent/i.test(allContent)
                    const hasAskedQuestion = assistantMsgs.some(m => /\[OPTIONS\]|\[MULTI-OPTIONS\]/i.test(m.content))
                    const hasPlanConfirm = messages.some(m => /proceed to generate|Are these settings correct|ready to generate|Yes, proceed|DEPLOYMENT SUMMARY|CONFIGURATION SUMMARY|Let me summarize/i.test(m.content))
                    const isGenerating = hasFiles || assistantMsgs.some(m => /TERRAFORM GENERATION|Generating.*Terraform|# File:|setup-aws|\.tf generated/i.test(m.content))

                    let currentIdx = 0
                    if (!hasAskedQuestion) currentIdx = 0
                    else if (hasAskedQuestion && !hasPlanConfirm && !isGenerating) currentIdx = 1
                    else if (hasPlanConfirm && !isGenerating) currentIdx = 2
                    if (isGenerating) currentIdx = 3
                    // Advance to Verify when terraform validate is running/done, when
                    // verification text appeared, OR when the agent finished generating.
                    // Use agentWorking (not sending) — sending flips false on the first
                    // streamed chunk while files are still being generated.
                    if (validating || validation || hasVerification || (hasFiles && !agentWorking)) currentIdx = 4

                    return (
                        <div className="px-6 py-3 border-b bg-card">
                            <div className="flex items-center">
                                {phases.map((p, i) => {
                                    const isVerifyStep = i === phases.length - 1
                                    // Verify goes green once terraform validate passes — the clear
                                    // "validated / ready to deploy" signal.
                                    const isDone = i < currentIdx || (isVerifyStep && verifyPassed)
                                    const isCurrent = i === currentIdx && !isDone
                                    const showSpinner = isVerifyStep && validating && !isDone
                                    return (
                                    <div key={p.key} className="flex items-center" style={{ flex: i < phases.length - 1 ? 1 : 'none' }}>
                                        <div className="flex flex-col items-center" style={{ minWidth: 32 }}>
                                            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold border-2 transition-all ${
                                                isDone
                                                    ? 'border-green-500 bg-green-500/20 text-green-700 dark:text-green-400'
                                                    : isCurrent
                                                        ? 'border-blue-500 bg-blue-500/20 text-blue-700 dark:text-blue-400'
                                                        : 'border-muted-foreground/30 bg-muted/50 text-muted-foreground'
                                            }`}>
                                                {isDone ? <Check className="h-4 w-4" /> : showSpinner ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : i + 1}
                                            </div>
                                            <span className={`text-[10px] mt-1 whitespace-nowrap font-medium ${
                                                isDone
                                                    ? 'text-green-700 dark:text-green-400'
                                                    : isCurrent
                                                        ? 'text-blue-700 dark:text-blue-400'
                                                        : 'text-muted-foreground'
                                            }`}>{p.label}</span>
                                        </div>
                                        {i < phases.length - 1 && (
                                            <div className={`flex-1 h-0.5 mx-1 mt-[-14px] transition-all ${
                                                isDone ? 'bg-green-500/40' : 'bg-muted-foreground/20'
                                            }`} />
                                        )}
                                    </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })()}
                {/* Messages */}
                <div ref={chatRef} onScroll={() => { if (!chatRef.current) return; scrolledUp.current = chatRef.current.scrollHeight - chatRef.current.scrollTop - chatRef.current.clientHeight > 100 }}
                    className="chat-message-area" style={{ flex: 1, minHeight: 0 }}>
                    {messages.map((m, i) => {
                        const isLastAssistant = m.role === 'assistant' && i === messages.length - 1 && !sending
                        const { cleanContent, options, multiSelect } = m.role === 'assistant' ? extractOptions(m.content) : { cleanContent: m.content, options: [], multiSelect: false }
                        // Strip code blocks from chat display — they show in the right panel
                        const hasCodeBlocks = m.codeBlocks && m.codeBlocks.length > 0
                        // Always strip code blocks from assistant messages — they show in the Code panel
                        const displayContent = m.role === 'assistant' ? stripCodeBlocks(cleanContent) : cleanContent

                        return (
                        <div key={i} className={`message ${m.role === 'user' ? 'user' : 'agent'} message-container`}>
                            <div className="message-sender">{m.role === 'user' ? t('iac.you') : <><img src="/images/vs-spinner.png" alt="" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />{t('iac.iacAgent')}</>}</div>
                            <div className="message-bubble">
                                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(displayContent || cleanContent) }} />
                                {/* Files show in right panel — no badges needed in chat */}
                            </div>
                            {/* Option buttons */}
                            {isLastAssistant && options.length > 0 && (
                                <div className="detail-buttons-container" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
                                    {multiSelect ? (
                                        <>
                                            {options.map((opt, oi) => {
                                                const isSelected = selectedOptions.has(opt)
                                                return (
                                                    <button key={oi}
                                                        className={`detail-button ${isSelected ? 'more-details-button' : ''}`}
                                                        style={isSelected ? { background: 'hsl(var(--primary) / 0.15)', borderColor: 'hsl(var(--primary))' } : undefined}
                                                        onClick={() => setSelectedOptions(prev => {
                                                            const next = new Set(prev)
                                                            next.has(opt) ? next.delete(opt) : next.add(opt)
                                                            return next
                                                        })}>
                                                        {isSelected && <Check className="h-3 w-3 mr-1 inline" />}{opt}
                                                    </button>
                                                )
                                            })}
                                            <button className="detail-button more-details-button"
                                                style={{ fontWeight: 600 }}
                                                disabled={selectedOptions.size === 0}
                                                onClick={() => { send([...selectedOptions].join(', ')); setSelectedOptions(new Set()) }}>
                                                Confirm Selection →
                                            </button>
                                        </>
                                    ) : (
                                        options.map((opt, oi) => (
                                            <button key={oi} className="detail-button more-details-button" onClick={() => send(opt)}>
                                                {opt}
                                            </button>
                                        ))
                                    )}
                                    {messages.filter(msg => msg.role === 'user').length > 1 && (
                                        <button className="detail-button" style={{ borderColor: 'hsl(var(--muted-foreground))', color: 'hsl(var(--muted-foreground))' }}
                                            onClick={() => send('Go back to the previous question')}>
                                            ← Go Back
                                        </button>
                                    )}
                                </div>
                            )}
                            {/* Go back button when no options */}
                            {isLastAssistant && options.length === 0 && !hasCodeBlocks && messages.filter(msg => msg.role === 'user').length > 1 && (
                                <div className="detail-buttons-container">
                                    <button className="detail-button" style={{ borderColor: 'hsl(var(--muted-foreground))', color: 'hsl(var(--muted-foreground))' }}
                                        onClick={() => send('Go back to the previous question')}>
                                        ← Go Back
                                    </button>
                                </div>
                            )}
                            <div className="message-meta">
                                <span className="message-time">{formatTime(m.timestamp)}</span>
                                {m.role === 'assistant' && <CopyButton text={m.content} />}
                            </div>
                        </div>
                        )
                    })}

                    {messages.length === 1 && messages[0].role === 'assistant' && !sending && !loaded && (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
                            <Button onClick={() => send('Please analyse this hierarchy and classify each object. Identify all IP addresses from the metadata and classify them as public-facing or internal. Then present your findings for my review.')}>
                                Start Analysis
                            </Button>
                        </div>
                    )}

                    {/* Verification in progress indicator */}
                    {!sending && agentWorking && messages.length > 0 && /verification|cross-provider|sending to independent/i.test(messages[messages.length - 1]?.content || '') && (
                        <div className="message agent message-container">
                            <div className="message-sender"><img src="/images/vs-spinner.png" alt="" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />{t('iac.iacAgent')}</div>
                            <div className="flex items-center gap-3 px-3 py-3">
                                <Loader2 className="h-5 w-5 animate-spin text-blue-600 flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-medium">Independent verification in progress...</p>
                                    <p className="text-xs text-muted-foreground">The terraform-verify agent is reviewing all generated files</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {sending && (
                        <div className="message agent message-container">
                            <div className="message-sender"><img src="/images/vs-spinner.png" alt="" style={{ width: 16, height: 16, display: 'inline-block', verticalAlign: 'middle', marginRight: 4 }} />{t('iac.iacAgent')}</div>
                            <div className="progress-panel" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px' }}>
                                <div className="progress-spinner">
                                    <DotSphereSpinner size={48} logoSrc="/images/vsl-logo.png" variant="light" />
                                </div>
                                <div className="progress-messages">
                                    {statusMsgs.length > 0 ? statusMsgs.slice(-3).map((s, i) => <div key={i} className="progress-item">{s}</div>) :
                                        <div className="progress-item">Thinking...</div>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Activate Clara — lives on the left, because deploy/fix iteration
                    happens in this chat. Only available once deployment has started or
                    the user has downloaded the Terraform to deploy themselves. */}
                {files.length > 0 && claraAvailable && (
                    <div className="flex items-center justify-end px-3 pt-2">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                            const platform = files.some(f => f.code.includes('azurerm')) ? 'Azure'
                                : files.some(f => f.code.includes('google_')) ? 'GCP' : 'AWS'
                            const contextSummary = [
                                `IaC Deployment Context: ${ctx?.serviceName || mainId} — ${platform}`,
                                `${files.length} Terraform files generated`,
                                `Objects: ${ctx?.objectCount || 0}, Versions: ${ctx?.versionCount || 0}`,
                                `Key files: ${files.map(f => f.filename).join(', ')}`,
                            ].join('\n')
                            aiChatPanel.open({ tab: 'clara', context: contextSummary })
                        }}>
                            <Eye className="h-3 w-3 mr-1" />Activate Clara
                        </Button>
                    </div>
                )}

                {/* Input */}
                <form onSubmit={e => { e.preventDefault(); send(input) }} className="px-3 py-2 border-t flex items-center gap-2">
                    <Input value={input} onChange={e => setInput(e.target.value)}
                        placeholder={files.length > 0 ? t('iac.askDeployment') : t('iac.askInfrastructure')}
                        disabled={sending || !sid} className="flex-1 rounded-full px-4" />
                    <button type="submit" disabled={sending || !input.trim() || !sid}
                        className={`flex items-center justify-center w-8 h-8 rounded-md transition-colors flex-shrink-0 ${sending || !input.trim() || !sid ? 'opacity-50 cursor-not-allowed text-muted-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground cursor-pointer'}`}>
                        <SendHorizontal className="h-5 w-5" />
                    </button>
                </form>
            </div>

            {/* RIGHT: Terraform files */}
            <div className="w-1/2 flex flex-col overflow-hidden">
                <Tabs value={rightTab} onValueChange={v => setRightTab(v as any)} className="flex flex-col min-h-0 flex-1">
                    <TabsList className="w-full justify-start border-b rounded-none h-[72px] items-end p-0 bg-transparent">
                        <TabsTrigger value="code" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 gap-1.5">
                            <Code2 className="h-4 w-4 text-blue-600" />Code
                            {files.length > 0 && <Badge variant="secondary" className="text-[9px] ml-1">{files.length}</Badge>}
                        </TabsTrigger>
                        <TabsTrigger value="vis" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-2 gap-1.5">
                            <Eye className="h-4 w-4 text-blue-600" />Overview
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="code" className="flex-1 flex overflow-hidden m-0 data-[state=inactive]:hidden">
                        {files.length === 0 ? (
                            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                                <div className="text-center"><FileCode className="h-10 w-10 mx-auto mb-2 opacity-30" /><p>{t('iac.noTerraformFiles')}</p><p className="text-xs mt-1">{t('iac.filesAppear')}</p></div>
                            </div>
                        ) : (
                            <>
                                <div className="w-52 border-r overflow-y-auto py-1">
                                    {files.map((f, i) => (
                                        <button key={i}
                                            className={`w-full flex items-center gap-1.5 px-3 py-1.5 text-xs text-left hover:bg-accent/50 transition-colors ${i === activeIdx ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'}`}
                                            onClick={() => setActiveIdx(i)}>
                                            <FileCode className="h-3 w-3 flex-shrink-0" />
                                            <span className="truncate">{f.filename}</span>
                                        </button>
                                    ))}
                                </div>
                                <div className="flex-1 flex flex-col overflow-hidden">
                                    {active ? (
                                        <>
                                            <div className="flex items-center justify-between px-3 py-1.5 border-b">
                                                <span className="text-xs font-mono text-muted-foreground">{active.filename}</span>
                                                <div className="flex items-center gap-1">
                                                    <CopyButton text={active.code} />
                                                    <Button variant="ghost" size="sm" className="h-6 px-1" onClick={() => { setFiles(f => f.filter((_, j) => j !== activeIdx)); setActiveIdx(null) }}>
                                                        <Trash2 className="h-3 w-3 text-muted-foreground" />
                                                    </Button>
                                                </div>
                                            </div>
                                            <pre className="flex-1 overflow-auto p-4 text-xs font-mono leading-relaxed bg-muted/30 text-foreground">
                                                <code>{active.code}</code>
                                            </pre>
                                        </>
                                    ) : (
                                        <div className="flex-1 flex items-center justify-center text-muted-foreground text-xs">Select a file to view</div>
                                    )}
                                </div>
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="vis" className="flex-1 min-h-0 overflow-hidden m-0">
                        {(() => {
                            // Architecture diagram derived on demand from the CURRENT Terraform
                            // (it changes as the engineer requests updates). Module/resource tree
                            // is the loading/fallback state.
                            const tfFiles = files.map((f) => ({ filename: f.filename, code: f.code || '' }))
                            const mdFile = files.find((f) => /\.md$/i.test(f.filename))
                            const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')?.content || ''
                            const overviewMd = mdFile?.code || lastAssistant

                            if (tfFilesForArch.length === 0) {
                                return <IaCOverviewFlow files={tfFiles} markdown={overviewMd} />
                            }
                            if (archData) {
                                return (
                                    <div className="relative h-full w-full">
                                        <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
                                            {archStale && (
                                                <span className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-700 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
                                                    {t('iac.overviewStale', 'Terraform changed')}
                                                </span>
                                            )}
                                            <Button variant="outline" size="sm" onClick={buildArchitecture} disabled={archBuilding}>
                                                <RotateCcw className={archBuilding ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
                                                <span className="ml-1.5">{t('iac.overviewRebuild', 'Rebuild diagram')}</span>
                                            </Button>
                                        </div>
                                        <ArchitectureFlow architecture={archData} />
                                    </div>
                                )
                            }
                            if (archBuilding) {
                                return (
                                    <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        {t('iac.overviewBuilding', 'Building architecture diagram from Terraform…')}
                                    </div>
                                )
                            }
                            // Error or not yet built — show the tree with a build action.
                            return (
                                <div className="relative h-full w-full">
                                    <div className="absolute right-3 top-3 z-10">
                                        <Button variant="outline" size="sm" onClick={buildArchitecture} disabled={archBuilding}>
                                            <Eye className="h-4 w-4" />
                                            <span className="ml-1.5">{t('iac.overviewBuild', 'Build architecture diagram')}</span>
                                        </Button>
                                    </div>
                                    {archError && (
                                        <div className="absolute left-3 top-3 z-10 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                                            {archError}
                                        </div>
                                    )}
                                    <IaCOverviewFlow files={tfFiles} markdown={overviewMd} />
                                </div>
                            )
                        })()}
                    </TabsContent>
                </Tabs>

                {/* Deployment panel — always visible at bottom. The files-generated
                    summary sits here on the deployment side. */}
                <div className="border-t p-3 flex-shrink-0">
                    {files.length > 0 && (
                        <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="text-[9px] text-muted-foreground">{files.length} files generated</Badge>
                        </div>
                    )}
                    <DeploymentPanel
                        projectId={proj?.id || null}
                        cloudPlatform={proj?.cloudPlatform || 'aws'}
                        files={files}
                        mainId={mainId || ''}
                        scenario={scenario}
                        hierarchyContext={ctx || null}
                        verified={verifyPassed}
                        onAskAgentToFix={(errorMessage) => {
                            // Send the deployment error to the IaC agent for analysis
                            const fixPrompt = `The Terraform deployment failed with the following error. Please analyse the error, identify the root cause, and regenerate the affected files with the fix applied.\n\n${errorMessage}`
                            send(fixPrompt)
                        }}
                        onStatusChange={setDeployStatus}
                        onDownloaded={() => setDownloaded(true)}
                        busy={agentWorking}
                    />
                </div>
            </div>
            </div>

            {/* Restart confirmation */}
            {showRestart && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-card rounded-lg border p-6 shadow-lg max-w-md mx-4">
                        <h3 className="text-lg font-semibold mb-2">Start New Project?</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                            This will delete the current conversation and all generated Terraform files. This action cannot be undone.
                        </p>
                        <p className="text-sm font-medium mb-4">Do you want to continue?</p>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowRestart(false)}>No, Cancel</Button>
                            <Button variant="destructive" size="sm" onClick={async () => {
                                setShowRestart(false)
                                try {
                                    // Delete session from backend
                                    if (sid) {
                                        await fetch(`/api/ai/chat-history/session/${sid}`, { method: 'DELETE' }).catch(() => {})
                                    }
                                    // Delete project files
                                    if (proj?.id) {
                                        await iacApi.deleteProject(proj.id).catch(() => {})
                                    }
                                } catch {}
                                // Reset local state
                                setSid(null)
                                setMessages([])
                                setFiles([])
                                setActiveIdx(null)
                                setLoaded(false)
                                queryClient.invalidateQueries({ queryKey: ['iac-projects', mainId] })
                                toast({ title: t('iac.projectReset') })
                            }}>
                                Yes, Delete and Restart
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Navigation warning dialog */}
            <Dialog open={!!navWarning} onOpenChange={(open) => { if (!open) setNavWarning(null) }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t('iac.agentWorkingTitle')}</DialogTitle>
                        <DialogDescription>
                            {t('iac.agentWorkingDescription')}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setNavWarning(null)}>
                            {t('iac.stayOnPage')}
                        </Button>
                        <Button variant="destructive" onClick={() => {
                            const href = navWarning?.href
                            setNavWarning(null)
                            setAgentWorking(false)
                            if (href === 'back') {
                                navigate(-1)
                            } else if (href) {
                                navigate(href)
                            }
                        }}>
                            {t('iac.leavePage')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
